import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Filter, X } from 'lucide-react';
import type { Todo, Event, TaskStatus } from '../types';
import { TaskHorizon } from '../types';
import { eventsAPI } from '../services/api';
import { parseLocalTimestamp } from '../utils/dateUtils';

interface CalendarViewProps {
  todos: Todo[];
  onTodoClick: (todo: Todo) => void;
  onTodoStatusChange: (todoId: string, status: TaskStatus) => void;
  onTodoDateChange: (todoId: string, date: string | null, dateType: 'deadline') => void;
  onCreateTodo?: (dueDate: string) => void;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  todos: {
    deadline: Todo[];
  };
  events: Event[];
}

interface DateTypeInfo {
  type: 'deadline';
  color: string;
  bgColor: string;
  icon: string;
  label: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  todos,
  onTodoClick,
  onTodoDateChange,
  onCreateTodo
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedTodo, setDraggedTodo] = useState<Todo | null>(null);
  const [draggedDateType, setDraggedDateType] = useState<'deadline' | null>(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [horizonFilter, setHorizonFilter] = useState<string[]>([]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [dateTypeFilter, setDateTypeFilter] = useState<('deadline')[]>(['deadline']);
  const [showEvents, setShowEvents] = useState(true);

  // Compute visible month date range for event fetching
  const monthRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    // Extend to cover full weeks shown on calendar
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start_date: fmt(startDate), end_date: fmt(endDate) };
  }, [currentDate]);

  // Fetch events for the visible month range
  const { data: monthEvents = [] } = useQuery({
    queryKey: ['events', 'calendar', monthRange.start_date, monthRange.end_date],
    queryFn: () => eventsAPI.getAll({ start_date: monthRange.start_date, end_date: monthRange.end_date }),
  });

  // Date type configuration - moved inside useMemo to fix dependency warning
  const dateTypeConfig = useMemo(() => ({
    deadline: {
      type: 'deadline' as const,
      color: 'text-red-800',
      bgColor: 'bg-red-100 hover:bg-red-200 border-red-300',
      icon: '⏰',
      label: 'Deadline'
    },
  }), []);

  // Helper function to normalize date strings to YYYY-MM-DD format
  const normalizeDate = useCallback((dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    try {
      // Directly extract the date part to avoid timezone conversion issues
      // This handles both YYYY-MM-DD and YYYY-MM-DDTHH:MM:SS formats
      const datePart = dateString.split('T')[0];
      
      // Validate that it's a proper date format (YYYY-MM-DD)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        console.warn('Invalid date format detected:', dateString);
        return null;
      }
      
      return datePart;
    } catch (error) {
      console.error('Error normalizing date:', dateString, error);
      return null;
    }
  }, []);

  // Filter todos based on search and filters
  const filteredTodos = useMemo(() => {
    return todos.filter(todo => {
      // Search filter
      if (searchQuery && !todo.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
          !todo.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0 && !statusFilter.includes(todo.status)) {
        return false;
      }

      // Horizon filter
      if (horizonFilter.length > 0 && !horizonFilter.includes(todo.horizon || '')) {
        return false;
      }

      // Tags filter
      if (tagsFilter.length > 0 && !tagsFilter.some(tag => todo.tags.includes(tag))) {
        return false;
      }

      return true;
    });
  }, [todos, searchQuery, statusFilter, horizonFilter, tagsFilter]);

  // Generate calendar days for the current month
  const generateCalendarDays = useCallback((): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from the first Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    // End at the last Saturday of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const currentDay = new Date(date);
      // Use local date methods to avoid timezone conversion issues
      const yearStr = currentDay.getFullYear();
      const monthStr = String(currentDay.getMonth() + 1).padStart(2, '0');
      const dayStr = String(currentDay.getDate()).padStart(2, '0');
      const dateString = `${yearStr}-${monthStr}-${dayStr}`;
      
      // Filter todos for this date with improved date parsing
      const todosForDate = {
        deadline: dateTypeFilter.includes('deadline') ? filteredTodos.filter(todo => {
          const normalizedDate = normalizeDate(todo.deadline);
          return normalizedDate === dateString;
        }) : [],
      };
      
      // Filter events for this date
      const eventsForDate = showEvents ? monthEvents.filter(event => {
        const eventDate = event.start_time?.split('T')[0];
        return eventDate === dateString;
      }) : [];

      days.push({
        date: new Date(currentDay),
        isCurrentMonth: currentDay.getMonth() === month,
        isToday: currentDay.getTime() === today.getTime(),
        todos: todosForDate,
        events: eventsForDate
      });
    }
    
    return days;
  }, [currentDate, filteredTodos, normalizeDate, dateTypeFilter, monthEvents, showEvents]);

  // Helper function to consolidate todos that appear on the same day
  const getConsolidatedTodos = useCallback((dayTodos: { deadline: Todo[] }) => {
    const todoMap = new Map<string, {
      todo: Todo;
      dateTypes: DateTypeInfo[];
    }>();

    // Process each date type
    Object.entries(dayTodos).forEach(([dateType, todos]) => {
      todos.forEach(todo => {
        const key = todo.id;
        if (!todoMap.has(key)) {
          todoMap.set(key, {
            todo,
            dateTypes: []
          });
        }
        const config = dateTypeConfig[dateType];
        if (config && !todoMap.get(key)!.dateTypes.some(dt => dt.type === config.type)) {
          todoMap.get(key)!.dateTypes.push(config);
        }
      });
    });

    return Array.from(todoMap.values());
  }, [dateTypeConfig]);

  const calendarDays = generateCalendarDays();

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDragStart = (e: React.DragEvent, todo: Todo, dateType: 'deadline') => {
    e.stopPropagation();
    setDraggedTodo(todo);
    setDraggedDateType(dateType);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${todo.id}:${dateType}`);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    
    if (draggedTodo && draggedDateType) {
      // Use local date methods to avoid timezone conversion issues
      const yearStr = targetDate.getFullYear();
      const monthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
      const dayStr = String(targetDate.getDate()).padStart(2, '0');
      const dateString = `${yearStr}-${monthStr}-${dayStr}`;
      
      onTodoDateChange(draggedTodo.id, dateString, draggedDateType);
    }
    
    setDraggedTodo(null);
    setDraggedDateType(null);
  };

  const handleDragEnd = () => {
    setDraggedTodo(null);
    setDraggedDateType(null);
  };

  // Filter handlers
  const toggleStatusFilter = (status: TaskStatus) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const toggleHorizonFilter = (horizon: string) => {
    setHorizonFilter(prev =>
      prev.includes(horizon)
        ? prev.filter(h => h !== horizon)
        : [...prev, horizon]
    );
  };

  const toggleTagFilter = (tag: string) => {
    setTagsFilter(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const toggleDateTypeFilter = (dateType: 'deadline') => {
    setDateTypeFilter(prev => 
      prev.includes(dateType) 
        ? prev.filter(t => t !== dateType)
        : [...prev, dateType]
    );
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setHorizonFilter([]);
    setTagsFilter([]);
    setDateTypeFilter(['deadline']);
    setShowEvents(true);
    setSearchQuery('');
  };

  // Get all unique tags from todos
  const allTags = Array.from(new Set(todos.flatMap(todo => todo.tags))).sort();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Calendar Header with Search and Filters */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
            >
              Today
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks in calendar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-md flex items-center space-x-2 transition-colors ${
              showFilters || statusFilter.length > 0 || horizonFilter.length > 0 || tagsFilter.length > 0 || dateTypeFilter.length < 1 || !showEvents
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {(statusFilter.length > 0 || horizonFilter.length > 0 || tagsFilter.length > 0 || dateTypeFilter.length < 1 || !showEvents) && (
              <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                {statusFilter.length + horizonFilter.length + tagsFilter.length + (dateTypeFilter.length < 1 ? 1 : 0) + (!showEvents ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Filters</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Type Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Show Dates</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={dateTypeFilter.includes('deadline')}
                      onChange={() => toggleDateTypeFilter('deadline')}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 flex items-center">
                      <div className="w-2 h-2 bg-red-500 rounded mr-2"></div>
                      Deadline
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showEvents}
                      onChange={() => setShowEvents(!showEvents)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 flex items-center">
                      <div className="w-2 h-2 bg-indigo-500 rounded mr-2"></div>
                      Events
                    </span>
                  </label>
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                <div className="space-y-2">
                  {['not_started', 'in_progress', 'waiting', 'completed'].map(status => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={statusFilter.includes(status as TaskStatus)}
                        onChange={() => toggleStatusFilter(status as TaskStatus)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">
                        {status.replace('_', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Horizon Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Horizon</h4>
                <div className="space-y-2">
                  {Object.values(TaskHorizon).map(horizon => (
                    <label key={horizon} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={horizonFilter.includes(horizon)}
                        onChange={() => toggleHorizonFilter(horizon)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">
                        {horizon === 'now' ? '🔴 Now' :
                         horizon === 'next' ? '🔵 Next' :
                         horizon === 'soon' ? '🟡 Soon' :
                         horizon === 'later' ? '⚪ Later' :
                         '💤 Someday'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Tags</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {allTags.map(tag => (
                    <label key={tag} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tagsFilter.includes(tag)}
                        onChange={() => toggleTagFilter(tag)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {tag}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span>Deadline</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-indigo-500 rounded"></div>
          <span>Events</span>
        </div>
        <div className="text-gray-500">
          Showing {filteredTodos.length} of {todos.length} tasks
        </div>
        <div className="text-xs text-gray-400">
          Drag tasks to move deadlines
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {dayNames.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
                !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white'
              } ${day.isToday ? 'bg-blue-50' : ''}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, day.date)}
            >
              {/* Date Number - Clickable to create task */}
              <div 
                className={`text-sm font-medium mb-2 cursor-pointer hover:bg-blue-50 rounded px-1 py-0.5 transition-colors select-none ${
                  !day.isCurrentMonth ? 'text-gray-400 hover:text-blue-600' : 
                  day.isToday ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onCreateTodo) {
                    const dateStr = day.date.toISOString().split('T')[0];
                    onCreateTodo(dateStr);
                  }
                }}
                onDragStart={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                title="Click to create a task due on this date"
                style={{ userSelect: 'none' }}
              >
                {day.date.getDate()}
              </div>

              {/* Events */}
              {day.events.length > 0 && (
                <div className="space-y-1 mb-1">
                  {day.events.map((event) => (
                    <div
                      key={event.id}
                      className="text-xs p-1 bg-indigo-100 hover:bg-indigo-200 border-indigo-300 text-indigo-800 rounded border cursor-default"
                      title={`${event.title}${event.start_time ? ` - ${parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="text-xs">📅</span>
                        <div className="truncate flex-1">{event.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tasks */}
              <div className="space-y-1">
                {getConsolidatedTodos(day.todos).map((consolidatedTodo, consolidatedIndex) => {
                  const todo = consolidatedTodo.todo;
                  const dateTypes = consolidatedTodo.dateTypes;
                  
                  // If only one date type, show as before but draggable
                  if (dateTypes.length === 1) {
                    const dateType = dateTypes[0];
                    return (
                      <div
                        key={`single-${consolidatedIndex}`}
                        className={`text-xs p-1 ${dateType.bgColor} ${dateType.color} rounded cursor-pointer transition-colors border`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, todo, dateType.type)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onTodoClick(todo)}
                        title={`${dateType.label}: ${todo.title}`}
                      >
                        <div className="flex items-center space-x-1">
                          <span className="text-xs">{dateType.icon}</span>
                          <div className="truncate flex-1">{todo.title}</div>
                        </div>
                      </div>
                    );
                  }

                  // Multiple date types - show segmented view
                  return (
                    <div
                      key={`multi-${consolidatedIndex}`}
                      className="border border-gray-300 rounded overflow-hidden"
                    >
                      {/* Task title header */}
                      <div 
                        className="text-xs p-1 bg-gray-100 text-gray-800 cursor-pointer hover:bg-gray-200 transition-colors border-b border-gray-300"
                        onClick={() => onTodoClick(todo)}
                        title={`Click to view: ${todo.title}`}
                      >
                        <div className="truncate font-medium">{todo.title}</div>
                      </div>
                      
                      {/* Date type segments */}
                      <div className="flex">
                        {dateTypes.map((dateType, typeIndex) => (
                          <div
                            key={`${dateType.type}-${typeIndex}`}
                            className={`flex-1 text-xs p-1 ${dateType.bgColor} ${dateType.color} cursor-move transition-colors border-r border-gray-300 last:border-r-0 hover:opacity-80`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, todo, dateType.type)}
                            onDragEnd={handleDragEnd}
                            title={`Drag to move ${dateType.label} date for: ${todo.title}`}
                          >
                            <div className="flex items-center justify-center">
                              <span className="text-xs">{dateType.icon}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView; 